import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { authService } from '../../services/api'
import { User, LoginCredentials, Branch } from '../../types/api'
import { isApiError } from '../../utils/api'

// Define the state interface
interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  branches: Branch[]
  activeBranch: Branch | null
  permissions: string[]
  preferences: Record<string, any>
  loading: boolean
  error: string | null
  loginLoading: boolean
  logoutLoading: boolean
  refreshLoading: boolean
  sessionExpired: boolean
  twoFactorRequired: boolean
  twoFactorQrCode: string | null
  twoFactorEnabled: boolean
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: localStorage.getItem('auth_token'),
  branches: [],
  activeBranch: null,
  permissions: [],
  preferences: {},
  loading: false,
  error: null,
  loginLoading: false,
  logoutLoading: false,
  refreshLoading: false,
  sessionExpired: false,
  twoFactorRequired: false,
  twoFactorQrCode: null,
  twoFactorEnabled: false,
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Login failed')
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout()
      return true
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Logout failed')
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Token refresh failed')
    }
  }
)

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getProfile()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get profile')
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update profile')
    }
  }
)

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (data: {
    current_password: string
    new_password: string
    new_password_confirmation: string
  }, { rejectWithValue }) => {
    try {
      await authService.changePassword(data)
      return true
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to change password')
    }
  }
)

export const getBranches = createAsyncThunk(
  'auth/getBranches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getBranches()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get branches')
    }
  }
)

export const setActiveBranch = createAsyncThunk(
  'auth/setActiveBranch',
  async (branchId: string, { rejectWithValue }) => {
    try {
      const response = await authService.setActiveBranch(branchId)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to set active branch')
    }
  }
)

export const getPermissions = createAsyncThunk(
  'auth/getPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getPermissions()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get permissions')
    }
  }
)

export const getPreferences = createAsyncThunk(
  'auth/getPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getPreferences()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get preferences')
    }
  }
)

export const updatePreferences = createAsyncThunk(
  'auth/updatePreferences',
  async (preferences: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await authService.updatePreferences(preferences)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update preferences')
    }
  }
)

export const enableTwoFactor = createAsyncThunk(
  'auth/enableTwoFactor',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.enableTwoFactor()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to enable two-factor authentication')
    }
  }
)

export const confirmTwoFactor = createAsyncThunk(
  'auth/confirmTwoFactor',
  async (code: string, { rejectWithValue }) => {
    try {
      const response = await authService.confirmTwoFactor(code)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to confirm two-factor authentication')
    }
  }
)

export const disableTwoFactor = createAsyncThunk(
  'auth/disableTwoFactor',
  async (password: string, { rejectWithValue }) => {
    try {
      await authService.disableTwoFactor(password)
      return true
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to disable two-factor authentication')
    }
  }
)

// Create the slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setSessionExpired: (state, action: PayloadAction<boolean>) => {
      state.sessionExpired = action.payload
    },
    clearSession: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.token = null
      state.branches = []
      state.activeBranch = null
      state.permissions = []
      state.preferences = {}
      state.sessionExpired = false
      state.twoFactorRequired = false
      state.twoFactorQrCode = null
      state.twoFactorEnabled = false
    },
    setActiveBranchLocal: (state, action: PayloadAction<Branch>) => {
      state.activeBranch = action.payload
    },
    setPreference: (state, action: PayloadAction<{ key: string, value: any }>) => {
      state.preferences[action.payload.key] = action.payload.value
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loginLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loginLoading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
        state.error = null
        state.sessionExpired = false
      })
      .addCase(login.rejected, (state, action) => {
        state.loginLoading = false
        state.error = action.payload as string
        state.isAuthenticated = false
        state.user = null
        state.token = null
      })

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.logoutLoading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.logoutLoading = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.branches = []
        state.activeBranch = null
        state.permissions = []
        state.preferences = {}
        state.sessionExpired = false
        state.twoFactorRequired = false
        state.twoFactorQrCode = null
        state.twoFactorEnabled = false
      })
      .addCase(logout.rejected, (state, action) => {
        state.logoutLoading = false
        state.error = action.payload as string
      })

    // Refresh Token
    builder
      .addCase(refreshToken.pending, (state) => {
        state.refreshLoading = true
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.refreshLoading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.sessionExpired = false
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.refreshLoading = false
        state.error = action.payload as string
        state.sessionExpired = true
      })

    // Get Profile
    builder
      .addCase(getProfile.pending, (state) => {
        state.loading = true
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        state.isAuthenticated = true
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update Profile
    builder
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Get Branches
    builder
      .addCase(getBranches.fulfilled, (state, action) => {
        state.branches = action.payload
        if (action.payload.length > 0 && state.user) {
          const activeBranch = action.payload.find(b => b.id === state.user!.active_branch_id)
          if (activeBranch) {
            state.activeBranch = activeBranch
          }
        }
      })
      .addCase(getBranches.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Set Active Branch
    builder
      .addCase(setActiveBranch.fulfilled, (state, action) => {
        state.user = action.payload
        const activeBranch = state.branches.find(b => b.id === action.payload.active_branch_id)
        if (activeBranch) {
          state.activeBranch = activeBranch
        }
      })
      .addCase(setActiveBranch.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Get Permissions
    builder
      .addCase(getPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload
      })
      .addCase(getPermissions.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Get Preferences
    builder
      .addCase(getPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload
      })
      .addCase(getPreferences.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Update Preferences
    builder
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.preferences = action.payload
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Enable Two Factor
    builder
      .addCase(enableTwoFactor.fulfilled, (state, action) => {
        state.twoFactorQrCode = action.payload.qr_code
        state.twoFactorRequired = true
      })
      .addCase(enableTwoFactor.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Confirm Two Factor
    builder
      .addCase(confirmTwoFactor.fulfilled, (state) => {
        state.twoFactorEnabled = true
        state.twoFactorRequired = false
        state.twoFactorQrCode = null
      })
      .addCase(confirmTwoFactor.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Disable Two Factor
    builder
      .addCase(disableTwoFactor.fulfilled, (state) => {
        state.twoFactorEnabled = false
        state.twoFactorQrCode = null
      })
      .addCase(disableTwoFactor.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

// Export actions
export const { 
  clearError, 
  setSessionExpired, 
  clearSession, 
  setActiveBranchLocal, 
  setPreference 
} = authSlice.actions

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectUser = (state: { auth: AuthState }) => state.auth.user
export const selectBranches = (state: { auth: AuthState }) => state.auth.branches
export const selectActiveBranch = (state: { auth: AuthState }) => state.auth.activeBranch
export const selectPermissions = (state: { auth: AuthState }) => state.auth.permissions
export const selectPreferences = (state: { auth: AuthState }) => state.auth.preferences
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error

// Permission helper selector
export const selectHasPermission = (permission: string) => (state: { auth: AuthState }) => {
  return state.auth.permissions.includes(permission)
}

export const selectHasAnyPermission = (permissions: string[]) => (state: { auth: AuthState }) => {
  return permissions.some(permission => state.auth.permissions.includes(permission))
}

export default authSlice.reducer 